/**
 * Invite acceptance page for new users joining an account.
 * Validates tokens, stores pending invites, and redirects to OAuth login.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth";

/**
 * Invite acceptance page
 *
 * Handles the invite link flow:
 * 1. If user is not logged in, store token and redirect to login
 * 2. If user is logged in, accept the invite
 * 3. On success, redirect to dashboard
 */
export function InvitePage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { user, setUser, setTeams, setAccounts, setActiveTeam } = useAuthStore();
	const [error, setError] = useState<string | null>(null);
	const [isAccepting, setIsAccepting] = useState(false);

	const token = searchParams.get("token");

	// Fetch user profile when needed (to refresh brands after accepting)
	const meQuery = trpc.auth.me.useQuery(undefined, {
		enabled: false, // Manual trigger
	});

	// Accept invite mutation
	const acceptInviteMutation = trpc.auth.acceptInvite.useMutation({
		onSuccess: async () => {
			// Clear the stored token
			sessionStorage.removeItem("pendingInviteToken");

			// Refetch user profile to get updated brands and accounts
			const result = await meQuery.refetch();
			if (result.data) {
				const { brands, accounts, ...userData } = result.data;
				setUser(userData);
				setTeams(brands);
				setAccounts(accounts);

				// Set active brand to first one if not set
				if (brands.length > 0 && brands[0]) {
					setActiveTeam(brands[0]);
				}
			}

			// Navigate to dashboard
			void navigate("/dashboard", { replace: true });
		},
		onError: (err) => {
			setError(err.message);
			setIsAccepting(false);
		},
	});

	useEffect(() => {
		// Validate token exists
		if (!token) {
			setError("Invalid invite link - missing token");
			return;
		}

		// If user is not logged in, store token and redirect to login
		if (!user) {
			sessionStorage.setItem("pendingInviteToken", token);
			void navigate("/login", { replace: true });
			return;
		}

		// User is logged in - accept the invite
		if (!isAccepting && !acceptInviteMutation.isPending && !error) {
			setIsAccepting(true);
			acceptInviteMutation.mutate({ token });
		}
	}, [token, user, navigate, acceptInviteMutation, isAccepting, error]);

	// Show error state
	if (error) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background">
				<div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-8 shadow-lg text-center">
					<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
						<svg
							className="h-6 w-6 text-destructive"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</div>
					<h2 className="text-xl font-semibold">Unable to Accept Invite</h2>
					<p className="text-sm text-muted-foreground">{error}</p>
					<div className="flex flex-col gap-2 pt-4">
						<Button
							onClick={() => navigate("/login", { replace: true })}
							variant="outline"
						>
							Back to Login
						</Button>
						{user && (
							<Button
								onClick={() => navigate("/dashboard", { replace: true })}
							>
								Go to Dashboard
							</Button>
						)}
					</div>
				</div>
			</div>
		);
	}

	// Show loading state
	return (
		<div className="flex min-h-screen items-center justify-center bg-background">
			<div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-8 shadow-lg text-center">
				<div className="mx-auto flex h-12 w-12 items-center justify-center">
					<svg
						className="h-8 w-8 animate-spin text-primary"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
				</div>
				<h2 className="text-xl font-semibold">Accepting invite...</h2>
				<p className="text-sm text-muted-foreground">
					Please wait while we set up your account access
				</p>
			</div>
		</div>
	);
}
